import { makeStyles } from 'tss-react/mui';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, PAYLOAD_DATE_FORMAT,
  TextFieldForm,
  Typography,
} from 'web-library';
import { IOptionType } from '../../../../api/candidate/exam/model';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { SchoolFormData } from '../../../../models';
import { useAddSchool } from '../../logic/useAddSchool';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";
import dayjs from "dayjs";

const TYPE_SCHOOL_TEXT = 'exam->>NonNationalExam.TypeOfSchoolModal';
const SCHOOL_ATTENDING_TEXT = 'exam->>NonNationalExam.NameOfSchoolAttending';
const LEVEL_PASSED_TEXT = 'exam->>NonNationalExam.HighestLevelPassedModal';

const useStyles = makeStyles()((theme) =>
({
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  selectSubjectText: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  customSelect: {
    marginTop: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(5),
  },
  input: {
    marginTop: theme.spacing(4),
  },
}));

interface DialogButtonsProps {
  onDialogClose: () => void;
  apiButtonDisabled: boolean;
  apiButtonStatus: MutationStatus;
  apiButtonClick: () => void;
}

const DialogButtons = ({
  apiButtonDisabled,
  onDialogClose,
  apiButtonStatus,
  apiButtonClick,
}: DialogButtonsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <DialogActions id="candidate-dialog-action">
      <Box className={classes.buttonContainer}>
        <Button onClick={onDialogClose} variant="text">
          {t('specialConsideration->>Dialog.Cancel')}
        </Button>

        <ApiButton
          id="button-saveChanges"
          status={apiButtonStatus}
          onClick={apiButtonClick}
          disabled={apiButtonDisabled}
        >
          {t('components->>IconLabel.SaveChanges')}
        </ApiButton>
      </Box>
    </DialogActions>
  );
};

const DialogTitleContainer = () => {
  const { t } = useTranslation();
  return (
    <DialogTitle>
      <Typography variant="h4">{t('exam->>NonNationalExam.EditSchool')}</Typography>
    </DialogTitle>
  );
};

interface EditSchoolDialogProps {
  modalState: boolean;
  onDialogClose: () => void;
  onUpdateDataTable: (updateValue: SchoolFormData) => void;
  schoolSelected: SchoolFormData | undefined;
  birthCountryData: IOptionType[] | undefined;
  isAEIS: boolean;
}

const EditSchoolDialog = ({
  modalState,
  onDialogClose,
  schoolSelected,
  birthCountryData,
  isAEIS,
  onUpdateDataTable,
}: EditSchoolDialogProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { formHook } = useAddSchool(schoolSelected);

  const { control, formState: { errors }, watch, setValue, register, reset, trigger } = formHook;

  useEffect(() => {
    reset({ ...schoolSelected });
  }, [reset, schoolSelected]);

  const apiButtonClick = () => {
    formHook.handleSubmit((formData) => {
      if(dayjs(formHook.getValues('dateOfAdmission'), PAYLOAD_DATE_FORMAT)?.isAfter(dayjs(formHook.getValues('dateOfCompletion'), PAYLOAD_DATE_FORMAT))){
        formHook.setError('dateOfCompletion', {
          type: 'custom',
          message: 'Date of Completion should be later than Date of Admission',
        });
        return;
      }
      onUpdateDataTable(formData);
      onDialogClose();
    })();
  };

  return (
    <Dialog open={modalState} maxWidth="md" onClose={onDialogClose}>
      <DialogTitleContainer />
      <DialogContent>
        <TextFieldForm
          fullWidth
          variant="outlined"
          id="typeOfSchool"
          name="typeOfSchool"
          error={!!errors?.typeOfSchool}
          helperText={errors.typeOfSchool?.message?.toString()}
          title={t(TYPE_SCHOOL_TEXT)}
          placeholder={t('exam->>PlaceHolders.EnterTypeOfSchool')}
          label={t(TYPE_SCHOOL_TEXT)}
          form={formHook}
          isRequired
        />
        <TextFieldForm
          className={classes.input}
          fullWidth
          variant="outlined"
          id="schoolName"
          name="schoolName"
          error={!!errors?.schoolName}
          helperText={errors.schoolName?.message?.toString()}
          title={t(SCHOOL_ATTENDING_TEXT)}
          placeholder={t('exam->>PlaceHolders.EnterNameOfSchool')}
          label={t(SCHOOL_ATTENDING_TEXT)}
          form={formHook}
          isRequired
        />
        <ControlledAutocomplete
          className={classes.input}
          id="inputSelect-Country"
          name="country"
          control={control}
          label={t('exam->>NonNationalExam.Country')}
          options={birthCountryData || []}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
          error={!!errors?.country}
          helperText={errors?.country?.message?.toString()}
          disabled={isAEIS}
          isRequired
          disableClearable
          readOnly
        />
        <ControlledDatePicker
          className={classes.input}
          label={t('exam->>NonNationalExam.DateOfAdmission')}
          // value={dateOfAdmissionWatch || ''}
          onChange={(date) => {
            setValue('dateOfAdmission', date || '');
            trigger('dateOfAdmission');
          }}
          error={!!errors.dateOfAdmission}
          helperText={errors.dateOfAdmission?.message?.toString()}
          inputVariant="filled"
          autoOk
          isRequired
          control={control}
          name="dateOfAdmission"
        />
        <ControlledDatePicker
          className={classes.input}
          label={t('exam->>NonNationalExam.DateCompleting')}
          // value={dateCompletionWatch || ''}
          onChange={(date) => {
            setValue('dateOfCompletion', date || '');
            trigger('dateOfCompletion');
          }}
          error={!!errors.dateOfCompletion}
          helperText={errors.dateOfCompletion?.message?.toString()}
          inputVariant="filled"
          autoOk
          isRequired
          control={control}
          name="dateOfCompletion"
        />
        <TextFieldForm
          className={classes.input}
          fullWidth
          variant="outlined"
          id="highestLevelPassed"
          name="highestLevelPassed"
          error={!!errors.highestLevelPassed}
          helperText={errors.highestLevelPassed?.message?.toString()}
          title={t(LEVEL_PASSED_TEXT)}
          placeholder={t('exam->>PlaceHolders.EnterLevel')}
          label={t(LEVEL_PASSED_TEXT)}
          form={formHook}
          isRequired
        />
      </DialogContent>
      <DialogButtons
        apiButtonDisabled={false}
        apiButtonStatus="idle"
        onDialogClose={onDialogClose}
        apiButtonClick={apiButtonClick}
      />
    </Dialog>
  );
};

export default EditSchoolDialog;
